import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  ListTodo, FileText, BookOpen, Brain, Shield, Zap,
  CheckCircle, XCircle, Clock, Play, AlertTriangle,
  RefreshCw, Trash2, X, Save, Pencil
} from 'lucide-react';
import AgentLayout from '../components/AgentLayout';
import api from '../services/api';

const CAPABILITY_LABELS = {
  description_review: { label: 'Description Review', icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50' },
  kb_update: { label: 'Knowledge Update', icon: BookOpen, color: 'text-purple-600', bg: 'bg-purple-50' },
  loi_prompt: { label: 'LOI Prompt', icon: Brain, color: 'text-amber-600', bg: 'bg-amber-50' },
  compliance: { label: 'Compliance', icon: Shield, color: 'text-green-600', bg: 'bg-green-50' },
};

const STATUS_STYLES = {
  queued: { label: 'Queued', color: 'text-gray-600', bg: 'bg-gray-100', icon: Clock },
  running: { label: 'Running', color: 'text-blue-600', bg: 'bg-blue-100', icon: Play },
  completed: { label: 'Completed', color: 'text-green-600', bg: 'bg-green-100', icon: CheckCircle },
  failed: { label: 'Failed', color: 'text-red-600', bg: 'bg-red-100', icon: AlertTriangle },
  cancelled: { label: 'Cancelled', color: 'text-gray-500', bg: 'bg-gray-100', icon: X },
};

const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.queued;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.color}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
};

const CapabilityBadge = ({ capability }) => {
  const c = CAPABILITY_LABELS[capability] || { label: capability, icon: Zap, color: 'text-gray-600', bg: 'bg-gray-50' };
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.color}`}>
      <Icon className="w-3 h-3" /> {c.label}
    </span>
  );
};

const AdminTradosAgentTasks = () => {
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [taskFilter, setTaskFilter] = useState('');
  const [showNewTask, setShowNewTask] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editForm, setEditForm] = useState({ capability: '', priority: '', notes: '', target_id: '' });

  const fetchTasks = useCallback(async () => {
    setTasksLoading(true);
    try {
      const params = taskFilter ? `?status=${taskFilter}` : '';
      const res = await api.get(`/admin/trados-agent/tasks${params}`);
      setTasks(res.data.tasks || []);
    } catch (err) { toast.error('Failed to load tasks'); }
    finally { setTasksLoading(false); }
  }, [taskFilter]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const createTask = async (capability) => {
    try {
      await api.post('/admin/trados-agent/tasks', { capability, priority: 'normal' });
      toast.success('Task created');
      setShowNewTask(false);
      fetchTasks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create task'); }
  };

  const cancelTask = async (taskId) => {
    try {
      await api.delete(`/admin/trados-agent/tasks/${taskId}`);
      toast.success('Task cancelled');
      fetchTasks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to cancel task'); }
  };

  const startEditTask = (task) => {
    setEditingTask(task._id);
    setEditForm({
      capability: task.capability || '',
      priority: task.priority || 'normal',
      notes: task.notes || '',
      target_id: task.target_id || '',
    });
  };

  const saveEditTask = async () => {
    try {
      await api.put(`/admin/trados-agent/tasks/${editingTask}`, editForm);
      toast.success('Task updated');
      setEditingTask(null);
      fetchTasks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update task'); }
  };

  return (
    <AgentLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {['', 'queued', 'running', 'completed', 'failed'].map(f => (
              <button key={f}
                onClick={() => setTaskFilter(f)}
                className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                  taskFilter === f ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >{f || 'All'}</button>
            ))}
          </div>
          <button onClick={() => setShowNewTask(!showNewTask)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
          >
            <Zap className="w-4 h-4" /> New Task
          </button>
        </div>

        {showNewTask && (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm font-semibold text-gray-900 mb-3">Create New Task</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(CAPABILITY_LABELS).map(([key, cap]) => {
                const Icon = cap.icon;
                return (
                  <button key={key} onClick={() => createTask(key)}
                    className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-all text-left"
                  >
                    <Icon className={`w-5 h-5 ${cap.color}`} />
                    <span className="text-sm font-medium text-gray-700">{cap.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {tasksLoading ? (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading tasks...
            </div>
          ) : tasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <ListTodo className="w-8 h-8 mb-2" />
              <p className="text-sm">No tasks found</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Capability</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Priority</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Created</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Tokens</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map(task => (
                  <React.Fragment key={task._id}>
                    <tr className="border-b border-gray-100 hover:bg-gray-50/50">
                      <td className="px-4 py-3"><CapabilityBadge capability={task.capability} /></td>
                      <td className="px-4 py-3"><StatusBadge status={task.status} /></td>
                      <td className="px-4 py-3 text-gray-500 capitalize text-xs">{task.priority}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(task.created_at)}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{task.tokens_used?.toLocaleString() || '-'}</td>
                      <td className="px-4 py-3 text-right flex items-center justify-end gap-1">
                        {task.status === 'queued' && (
                          <>
                            <button onClick={() => startEditTask(task)}
                              className="text-indigo-500 hover:text-indigo-700 p-1 rounded hover:bg-indigo-50"
                              title="Edit task"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button onClick={() => cancelTask(task._id)}
                              className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                              title="Cancel task"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                    {editingTask === task._id && (
                      <tr className="border-b border-indigo-100 bg-indigo-50/30">
                        <td colSpan={6} className="px-4 py-3">
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Capability</label>
                              <select value={editForm.capability}
                                onChange={e => setEditForm(f => ({ ...f, capability: e.target.value }))}
                                className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                              >
                                {Object.entries(CAPABILITY_LABELS).map(([key, cap]) => (
                                  <option key={key} value={key}>{cap.label}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                              <select value={editForm.priority}
                                onChange={e => setEditForm(f => ({ ...f, priority: e.target.value }))}
                                className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                              >
                                <option value="low">Low</option>
                                <option value="normal">Normal</option>
                                <option value="high">High</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Target ID</label>
                              <input type="text" value={editForm.target_id} placeholder="Optional"
                                onChange={e => setEditForm(f => ({ ...f, target_id: e.target.value }))}
                                className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                              <input type="text" value={editForm.notes} placeholder="Optional"
                                onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                                className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                              />
                            </div>
                          </div>
                          <div className="flex justify-end gap-2 mt-3">
                            <button onClick={() => setEditingTask(null)}
                              className="px-3 py-1.5 text-xs text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                            >Cancel</button>
                            <button onClick={saveEditTask}
                              className="px-3 py-1.5 text-xs text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center gap-1"
                            ><Save className="w-3 h-3" /> Save</button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AgentLayout>
  );
};

export default AdminTradosAgentTasks;
